import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { UserNoteResponse } from '../../../types';
import { format } from 'date-fns';

interface NoteItemProps {
  note: UserNoteResponse;
  onUpdate: (
    noteId: string,
    text: string,
    is_task: boolean,
    is_done: boolean
  ) => Promise<void>;
  onDelete: (noteId: string) => Promise<void>;
}

const NoteItem: React.FC<NoteItemProps> = ({ note, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(note.text);
  const [editIsTask, setEditIsTask] = useState(note.is_task);
  const [editIsDone, setEditIsDone] = useState(note.is_done);

  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);


  useEffect(() => {
    setEditText(note.text);
    setEditIsTask(note.is_task);
    setEditIsDone(note.is_done);
  }, [note]);



  const handleSave = async () => {
    if (!editText.trim()) return;

    setIsUpdating(true);
    try {
      await onUpdate(
        note.id,
        editText.trim(),
        editIsTask,
        editIsDone
      );
      setIsEditing(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const toggleTask = async () => {
    setIsUpdating(true);
    try {
      await onUpdate(
        note.id,
        note.text,
        !note.is_task,     // toggle is_task
        note.is_done && !(!note.is_task) // if turning OFF task, clear done
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const toggleDone = async () => {
    setIsUpdating(true);
    try {
      await onUpdate(
        note.id,
        note.text,
        note.is_task,
        !note.is_done
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(note.id);
      setShowDeleteDialog(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        {isEditing ? (
          <Box>
            {/* EDITING MODE */}
            <TextField
              fullWidth
              multiline
              rows={3}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              disabled={isUpdating}
              sx={{ mb: 2 }}
            />



            {editIsTask && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editIsDone}
                    onChange={() => setEditIsDone(!editIsDone)}
                  />
                }
                label="Mark as done"
              />
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setEditText(note.text);
                  setEditIsTask(note.is_task);
                  setEditIsDone(note.is_done);
                  setIsEditing(false);
                }}
                disabled={isUpdating}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isUpdating || !editText.trim()}
                startIcon={isUpdating ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {isUpdating ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {/* VIEW MODE */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1, pr: 2 }}>
                
                {/* Toggle "This is a task" */}
                {note.is_task && (
                  <Box
                    sx={{
                      display: "inline-block",
                      px: 1,
                      py: 0.5,
                      mb: 1,
                      borderRadius: 1,
                      fontSize: "12px",
                      fontWeight: 600,
                      backgroundColor: "#FFF3CD",
                      color: "#8A6D3B",
                      marginRight: 1,
                    }}
                  >
                    TASK
                  </Box>
                )}


                {/* Toggle completion ONLY if it's a task */}
                {note.is_task && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={note.is_done}
                        disabled={isUpdating}
                        onChange={toggleDone}
                      />
                    }
                    label="Mark as done"
                    sx={{ ml: 0 }}
                  />
                )}


                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                  {note.text}
                </Typography>
              </Box>

              <Box>
                <IconButton size="small" onClick={() => setIsEditing(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {formatDate(note.created_at)}
              {note.updated_at !== note.created_at && ' (edited)'}
            </Typography>
          </>
        )}
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Note</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NoteItem;
